// 택배 주소 출력 모달 — 출고관리 "주소출력" 버튼에서 연다.
//  - 보내는 사람: 환경설정 > 회사정보(company_info)에서 자동으로 가져온다.
//  - 받는 사람  : 업체관리(고객사/협력사)에서 검색해서 여러 곳을 동시에 고를 수 있다. (직접 입력/수정도 가능)
//  - 출력       : ESC/POS 래스터 비트맵으로 드라이버 없이 바로 출력 (연결 프로그램 / WebUSB / LAN / 시리얼), 또는 Windows 브라우저 인쇄
//  - 설정       : 헤더의 [설정] 버튼 → 연결방식·라벨크기·여백·글자크기·출력매수 (이 PC에 계속 저장됨)
//  출력 형식
//    [보내는 사람]
//    회사이름 / 연락처
//    회사주소
//
//    [받는사람]
//    회사이름 / 담당자 (연락처)
//    회사주소
import { useEffect, useMemo, useState } from 'react';
import API from '../api';

const MODES = [
  { key: 'agent',   label: '이 PC의 USB 프린터 (연결 프로그램, 권장)' },
  { key: 'usb',     label: 'USB 직접 (브라우저 WebUSB)' },
  { key: 'lan',     label: '네트워크 (LAN, IP:9100)' },
  { key: 'serial',  label: '시리얼 (COM / 블루투스)' },
  { key: 'windows', label: 'Windows 프린터 (드라이버 설치된 경우)' },
];
const DPI_DOTS_PER_MM = 8; // 203dpi
const DEF_PAD_X = 5; // 좌우 여백 기본값(mm)
const DEF_PAD_Y = 4; // 상하 여백 기본값(mm)

// ── ESC/POS 래스터 변환 ──────────────────────────────────────────────
const wrapLines = (ctx, text, maxW) => {
  const out = [];
  String(text || '').split('\n').forEach(par => {
    let line = '';
    for (const ch of par) {
      if (ctx.measureText(line + ch).width > maxW && line) { out.push(line); line = ch; } else line += ch;
    }
    out.push(line);
  });
  return out;
};

// 라벨 내용을 캔버스(흑백)로 그린다. padXmm/padYmm: 좌우/상하 여백(mm)
const nowStamp = () => {
  const d = new Date(); const p = (n) => String(n).padStart(2, '0');
  return `출력일시: ${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const renderLabelCanvas = ({ widthDots, heightDots, fontPt, blocks, padXmm, padYmm, paperDots, shiftMm, header }) => {
  const px = Math.max(12, Math.round(fontPt * 203 / 72));
  const padX = Math.max(0, Math.round((padXmm == null ? DEF_PAD_X : padXmm) * DPI_DOTS_PER_MM));
  const padY = Math.max(0, Math.round((padYmm == null ? DEF_PAD_Y : padYmm) * DPI_DOTS_PER_MM));
  const probe = document.createElement('canvas').getContext('2d');
  probe.font = `${px}px 'Malgun Gothic','맑은 고딕',sans-serif`;
  const maxW = Math.max(40, widthDots - padX * 2);
  const items = []; // {text, bold} / {sep:true}
  blocks.forEach((b, i) => {
    if (i > 0) items.push({ sep: true });
    items.push({ text: b.title, bold: true });
    b.lines.forEach(t => { if (t) wrapLines(probe, t, maxW).forEach(l => items.push({ text: l })); });
    if (b.note) { items.push({ gap: Math.round(10 * 203 / 72) }); wrapLines(probe, b.note, maxW).forEach(l => items.push({ text: l })); } // 비고 위 10pt 간격
  });
  const lh = Math.round(px * 1.4);
  const sepH = Math.round(px * 1.2);
  const contentH = items.reduce((s, it) => s + (it.sep ? sepH : it.gap ? it.gap : lh), 0);
  const hpx = Math.max(10, Math.round(px * 0.8)); // 출력일시 글자 크기(본문보다 약간 작게)
  const headH = header ? Math.round(hpx * 1.5) + Math.round(3 * 203 / 72) : 0; // 출력일시 아래 3pt 간격
  const H = Math.max(heightDots || 0, contentH + headH + padY * 2);
  // 라벨 영역(widthDots)을 용지 인쇄폭(paperDots) 가운데에 놓는다 → 좌우 여백이 같아짐 (shiftMm로 미세 보정)
  const W = Math.ceil(Math.max(widthDots, paperDots || 0) / 8) * 8;
  let ox = Math.floor((W - widthDots) / 2) + Math.round((shiftMm || 0) * DPI_DOTS_PER_MM);
  ox = Math.max(0, Math.min(W - widthDots, ox));
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = '#000'; g.textBaseline = 'top';
  if (header) {
    g.font = `${hpx}px 'Malgun Gothic','맑은 고딕',sans-serif`;
    g.fillText(header, ox + padX, padY);
  }
  let y = Math.max(padY + headH, padY + headH + Math.floor((H - padY * 2 - headH - contentH) / 2));
  items.forEach(it => {
    if (it.gap) {
      y += it.gap;
    } else if (it.sep) {
      g.setLineDash([8, 6]); g.lineWidth = 2; g.beginPath(); g.moveTo(ox + padX, y + sepH / 2); g.lineTo(ox + widthDots - padX, y + sepH / 2); g.stroke();
      y += sepH;
    } else {
      g.font = `${it.bold ? '800 ' : ''}${px}px 'Malgun Gothic','맑은 고딕',sans-serif`;
      g.fillText(it.text, ox + padX, y + (lh - px) / 2); y += lh;
    }
  });
  return c;
};

// 여러 라벨을 한 장(연속 용지)으로 세로로 이어 붙인다. gap: 라벨 사이 빈 칸(dot)
const stackCanvases = (cs, gap) => {
  const W = Math.max(...cs.map(x => x.width));
  const H = cs.reduce((s, x) => s + x.height, 0) + gap * (cs.length - 1);
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
  let y = 0; cs.forEach(x => { g.drawImage(x, 0, y); y += x.height + gap; });
  return c;
};

// 캔버스 → ESC/POS 바이트 (GS v 0 래스터 비트이미지, 256행씩 분할)
const canvasToEscPos = (canvas, copies, feedLines, cut) => {
  const w = canvas.width, h = canvas.height;
  const bytesPerRow = Math.ceil(w / 8);
  const img = canvas.getContext('2d').getImageData(0, 0, w, h).data;
  const rows = new Uint8Array(bytesPerRow * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const lum = 0.299 * img[i] + 0.587 * img[i + 1] + 0.114 * img[i + 2];
      if (lum < 160) rows[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  const parts = [];
  const push = (arr) => parts.push(Uint8Array.from(arr));
  for (let n = 0; n < copies; n++) {
    push([0x1b, 0x40]); // ESC @ 초기화
    for (let y0 = 0; y0 < h; y0 += 256) {
      const bh = Math.min(256, h - y0);
      push([0x1d, 0x76, 0x30, 0x00, bytesPerRow & 255, bytesPerRow >> 8, bh & 255, bh >> 8]);
      parts.push(rows.subarray(y0 * bytesPerRow, (y0 + bh) * bytesPerRow));
    }
    push([0x1b, 0x64, Math.max(0, Math.min(255, feedLines))]); // ESC d n 급지
    if (cut) push([0x1d, 0x56, 0x42, 0x00]); // GS V 66 0 커팅
  }
  return concatBytes(parts);
};
const concatBytes = (parts) => {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total); let o = 0;
  parts.forEach(p => { out.set(p, o); o += p.length; });
  return out;
};

const toBase64 = (u8) => { let s = ''; for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000)); return btoa(s); };

// ── 전송: 이 PC의 연결 프로그램(ims-label-agent, 127.0.0.1:9101) → Windows USB 인쇄 지원으로 직접 전송 ──
const AGENT = 'http://127.0.0.1:9101';
const AGENT_ERR = 'IMS 라벨프린터 연결 프로그램에 연결할 수 없습니다. 이 PC에서 "IMS_라벨프린터_연결프로그램"을 실행했는지 확인하세요.';
const agentDevices = async () => {
  let r; try { r = await fetch(AGENT + '/devices'); } catch (e) { throw new Error(AGENT_ERR); }
  const j = await r.json(); return j.devices || [];
};
const sendAgent = async (bytes, device) => {
  let r;
  try { r = await fetch(AGENT + '/print', { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ data: toBase64(bytes), device }) }); }
  catch (e) { throw new Error(AGENT_ERR); }
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || '프린터 전송 실패');
};

// ── 전송: USB(WebUSB) ──
let usbDev = null;
const sendUsb = async (bytes) => {
  if (!navigator.usb) throw new Error('이 브라우저/접속 주소에서는 USB 직접 출력을 사용할 수 없습니다. (Chrome/Edge + https 또는 localhost 필요) → 네트워크/시리얼/Windows 프린터 방식을 이용하세요.');
  if (!usbDev) {
    const known = await navigator.usb.getDevices();
    usbDev = known[0] || await navigator.usb.requestDevice({ filters: [] });
  }
  try {
    await usbDev.open();
    if (usbDev.configuration === null) await usbDev.selectConfiguration(1);
    let iface = null, ep = null;
    for (const i of usbDev.configuration.interfaces) {
      for (const a of i.alternates) {
        const out = a.endpoints.find(e => e.direction === 'out' && e.type === 'bulk');
        if (out && (a.interfaceClass === 7 || !iface)) { iface = i; ep = out; if (a.interfaceClass === 7) break; }
      }
    }
    if (!iface) throw new Error('출력 가능한 USB 엔드포인트를 찾지 못했습니다.');
    await usbDev.claimInterface(iface.interfaceNumber);
    for (let o = 0; o < bytes.length; o += 16384) await usbDev.transferOut(ep.endpointNumber, bytes.subarray(o, o + 16384));
    try { await usbDev.releaseInterface(iface.interfaceNumber); } catch (e) {}
  } catch (e) {
    try { await usbDev.close(); } catch (e2) {}
    const m = String((e && e.message) || e);
    usbDev = null;
    if (/access denied|claimInterface|protected/i.test(m)) throw new Error('Windows가 이 USB 프린터를 자체 프린터 드라이버로 점유 중이라 브라우저가 직접 열 수 없습니다. → "연결 프로그램" 또는 "네트워크(LAN)" 방식을 사용하세요.');
    throw e;
  }
  try { await usbDev.close(); } catch (e) {}
};

// ── 전송: 시리얼(Web Serial) ──
const sendSerial = async (bytes, baud) => {
  if (!navigator.serial) throw new Error('이 브라우저/접속 주소에서는 시리얼 출력을 사용할 수 없습니다. (Chrome/Edge + https 또는 localhost 필요)');
  const ports = await navigator.serial.getPorts();
  const port = ports[0] || await navigator.serial.requestPort();
  await port.open({ baudRate: Number(baud) || 9600 });
  const wr = port.writable.getWriter();
  try { for (let o = 0; o < bytes.length; o += 1024) await wr.write(bytes.subarray(o, o + 1024)); }
  finally { wr.releaseLock(); await port.close(); }
};

const PRESETS = [
  { key: '100x150', label: '100 × 150mm (택배 송장 표준)', w: 100, h: 150 },
  { key: '100x100', label: '100 × 100mm', w: 100, h: 100 },
  { key: '80x60',   label: '80 × 60mm', w: 80, h: 60 },
  { key: '58x40',   label: '58mm 용지 (XP-58 계열, 폭 48mm 인쇄)', w: 48, h: 40, paper: 384 },
  { key: '80x50',   label: '80mm 용지 좁게 (512 dot, POS-80)', w: 64, h: 30, paper: 576 },
  { key: '80x72',   label: '80mm 용지 넓게 (576 dot, POS-80)', w: 72, h: 30, paper: 576 },
  { key: '70x50',   label: '70 × 50mm', w: 70, h: 50 },
  { key: 'custom',  label: '직접 입력', w: 100, h: 150 },
];
const LS_KEY = 'ims_addr_print_opts';

const inp = { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', width: '100%' };
const lbl = { fontSize: 13, color: '#555', marginBottom: 4, display: 'block', fontWeight: 600 };
const btnLine = { border: '1px solid #ddd', background: '#fff', borderRadius: 8, padding: '0 14px', cursor: 'pointer', fontSize: 13 };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const num = (v, d) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

// 설정은 이 PC(브라우저)에 계속 저장한다. (프린터는 PC마다 다르므로)
const loadOpts = () => {
  try { const s = localStorage.getItem(LS_KEY) || sessionStorage.getItem(LS_KEY); return JSON.parse(s || '{}') || {}; } catch (e) { return {}; }
};

let rkSeq = 1;
const SHIP_TYPES = ['택배', '화물'];
const blankRecv = (note = '') => ({ key: 'r' + (rkSeq++), cid: null, name: '', manager: '', phone: '', address: '', note });
const recvLine = (r) => {
  const person = r.manager ? (r.phone ? `${r.manager} (${r.phone})` : r.manager) : (r.phone ? `(${r.phone})` : '');
  return [r.name, person].filter(Boolean).join(' / ');
};

export default function AddressPrintModal({ onClose }) {
  const saved = useMemo(loadOpts, []);
  const [sender, setSender] = useState({ name: '', phone: '', address: '' });
  const [receivers, setReceivers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('고객사');
  const [showList, setShowList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preset, setPreset] = useState(saved.preset || '80x50');
  const [w, setW] = useState(saved.w || 64);
  const [h, setH] = useState(saved.h || 30);
  const [fontPt, setFontPt] = useState(saved.fontPt || 16);
  const [copies, setCopies] = useState(saved.copies || 1);
  const [paperDots, setPaperDots] = useState(saved.paperDots != null ? saved.paperDots : ((PRESETS.find(x => x.key === (saved.preset || '80x50')) || {}).paper || 0));
  const [shiftX, setShiftX] = useState(saved.shiftX || 0);
  const [padX, setPadX] = useState(saved.padX == null ? DEF_PAD_X : saved.padX);
  const [padY, setPadY] = useState(saved.padY == null ? DEF_PAD_Y : saved.padY);
  const [withSender, setWithSender] = useState(saved.withSender !== false);
  // 배송 구분(택배/화물) — 선택한 값이 받는사람 비고에 자동 표기된다(비고는 개별 수정 가능)
  const [shipType, setShipType] = useState(SHIP_TYPES.includes(saved.shipType) ? saved.shipType : (saved.shipType === '' ? '' : '택배'));
  const onShipType = (t0) => {
    const t = shipType === t0 ? '' : t0; // 같은 버튼을 다시 누르면 선택 해제
    setShipType(t);
    setReceivers(prev => prev.map(r => (!r.note || SHIP_TYPES.includes(r.note)) ? { ...r, note: t } : r));
  };
  // 받는 곳이 2곳 이상일 때 보내는 사람: 'once' = 첫 장에만 한번, 'repeat' = 장마다 반복
  const [senderMode, setSenderMode] = useState(saved.senderMode === 'repeat' ? 'repeat' : 'once');
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(saved.mode || 'agent');
  const [lanHost, setLanHost] = useState(saved.lanHost || '');
  const [lanPort, setLanPort] = useState(saved.lanPort || 9100);
  const [baud, setBaud] = useState(saved.baud || 9600);
  const [cut, setCut] = useState(saved.cut !== false);
  const [devices, setDevices] = useState([]);
  const [device, setDevice] = useState(saved.device || '');
  const [deviceName, setDeviceName] = useState(saved.deviceName || '');
  const [agentOk, setAgentOk] = useState(null); // null=확인전, true/false
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, text}

  // 설정 저장 (이 PC에 계속 유지)
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ preset, w, h, fontPt, copies, padX, padY, paperDots, shiftX, withSender, senderMode, shipType, mode, lanHost, lanPort, baud, cut, device, deviceName }));
    } catch (e) {}
  }, [preset, w, h, fontPt, copies, padX, padY, paperDots, shiftX, withSender, senderMode, shipType, mode, lanHost, lanPort, baud, cut, device, deviceName]);

  const findDevices = async () => {
    setResult(null);
    try {
      const list = await agentDevices(); setDevices(list); setAgentOk(true);
      const usb = list.filter(d => !String(d.id).startsWith('printer:'));
      // 저장된 프린터가 있으면 그대로 유지, 없을 때만 USB 프린터를 자동 선택
      if (!list.find(d => d.id === device) && usb.length) setDevice(usb[0].id);
      if (usb.length) setResult(null);
      else if (list.length) setResult({ ok: false, text: '연결 프로그램은 실행 중이지만 USB 프린터는 찾지 못했습니다. (Windows 프린터 ' + list.length + '개만 확인됨) 프린터 전원/케이블을 확인하세요.' });
      else setResult({ ok: false, text: '연결 프로그램은 실행 중이지만 프린터를 찾지 못했습니다. 전원/케이블을 확인하세요.' });
    } catch (e) { setDevices([]); setAgentOk(false); setResult({ ok: false, text: e.message }); }
  };
  useEffect(() => { if (mode === 'agent') findDevices(); /* eslint-disable-next-line */ }, [mode]);
  // 선택된 프린터 이름 기억
  useEffect(() => {
    const d = devices.find(x => x.id === device);
    if (d && d.name !== deviceName) setDeviceName(d.name);
  }, [devices, device]); // eslint-disable-line

  // 보내는 사람 — 환경설정 회사정보
  useEffect(() => {
    API.get('/company-info').then(r => {
      const d = r.data || {};
      setSender({ name: d.company_name || '', phone: d.phone || '', address: d.address || '' });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // 받는 사람 후보 — 업체관리 (고객사/협력사)
  useEffect(() => {
    API.get('/companies/customers-lookup', { params: { category } })
      .then(r => setCompanies(r.data || [])).catch(() => setCompanies([]));
  }, [category]);

  const hits = useMemo(() => {
    const k = q.trim();
    const list = k ? companies.filter(c => (c.company_name || '').includes(k) || (c.manager_name || '').includes(k)) : companies;
    return list.slice(0, 50);
  }, [companies, q]);

  const isPicked = (c) => receivers.some(r => r.cid === category + ':' + c.id);
  const fromCompany = (c) => ({ key: 'r' + (rkSeq++), cid: category + ':' + c.id, name: c.company_name || '', manager: c.manager_name || '', phone: c.manager_contact || '', address: c.biz_address || '', note: shipType });
  const toggleCompany = (c) => {
    setReceivers(prev => prev.some(r => r.cid === category + ':' + c.id) ? prev.filter(r => r.cid !== category + ':' + c.id) : [...prev, fromCompany(c)]);
  };
  const pickAllHits = () => {
    setReceivers(prev => {
      const have = new Set(prev.map(r => r.cid));
      return [...prev, ...hits.filter(c => !have.has(category + ':' + c.id)).map(fromCompany)];
    });
  };
  const updRecv = (key, patch) => setReceivers(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));
  const delRecv = (key) => setReceivers(prev => prev.filter(r => r.key !== key));

  const onPreset = (key) => {
    setPreset(key);
    const p = PRESETS.find(x => x.key === key);
    if (p && key !== 'custom') { setW(p.w); setH(p.h); setPaperDots(p.paper || 0); }
  };

  const senderLine1 = [sender.name, sender.phone].filter(Boolean).join(' / ');
  const validRecv = receivers.filter(r => r.name || r.address);
  const canPrint = validRecv.length > 0 && w > 0 && h > 0;
  const nCopies = Math.max(1, Math.min(200, Number(copies) || 1));
  const px = Math.max(0, Math.min(30, num(padX, DEF_PAD_X)));
  const py = Math.max(0, Math.min(30, num(padY, DEF_PAD_Y)));

  const blocksFor = (r, showSender = withSender) => {
    const b = [];
    if (showSender) b.push({ title: '[보내는 사람]', lines: [senderLine1, sender.address] });
    b.push({ title: '[받는사람]', lines: [recvLine(r), r.address], note: r.note ? `비고: ${r.note}` : '' });
    return b;
  };
  const widthDots = Math.ceil(w * DPI_DOTS_PER_MM / 8) * 8;
  const makeCanvas = (r, showSender, header) => renderLabelCanvas({ widthDots, heightDots: Math.round(h * DPI_DOTS_PER_MM), fontPt, blocks: blocksFor(r, showSender), padXmm: px, padYmm: py, paperDots: Number(paperDots) || 0, shiftMm: num(shiftX, 0), header: header || nowStamp() });
  const stackMode = withSender && senderMode === 'once';
  const stackGap = Math.round(Math.max(12, Math.round(fontPt * 203 / 72)) * 1.4);
  // 출력 순서대로 [받는 사람, 보내는 사람 표시 여부] — 'once'면 받는 곳이 여러 곳이어도 맨 첫 장에만 보내는 사람
  const labelPlan = (list, copiesEach) => {
    const plan = [];
    list.forEach(r => { for (let n = 0; n < copiesEach; n++) plan.push(r); });
    return plan.map((r, i) => ({ r, showSender: withSender && (senderMode === 'repeat' || list.length < 2 || i === 0) }));
  };

  // 미리보기 — 실제 출력과 같은 캔버스를 이미지로 표시
  const previews = useMemo(() => {
    const list = receivers.length ? receivers : [{ key: 'sample', name: '(받는 사람을 선택하세요)', manager: '', phone: '', address: '' }];
    try {
      const plan = labelPlan(list, 1);
      if (stackMode && plan.length > 1) {
        const cv = stackCanvases(plan.map(({ r, showSender }) => makeCanvas(r, showSender)), stackGap);
        return [{ key: 'stack', name: '', caption: '연속 출력 (한 번에 출력 후 마지막에 한 번 커팅)', url: cv.toDataURL('image/png') }];
      }
      return plan.map(({ r, showSender }) => ({ key: r.key, name: r.name, url: makeCanvas(r, showSender).toDataURL('image/png') }));
    } catch (e) { return []; }
    // eslint-disable-next-line
  }, [receivers, sender, withSender, senderMode, w, h, fontPt, px, py, paperDots, shiftX]);

  const sendBytes = async (bytes) => {
    if (mode === 'agent') await sendAgent(bytes, device);
    else if (mode === 'usb') await sendUsb(bytes);
    else if (mode === 'serial') await sendSerial(bytes, baud);
    else if (mode === 'lan') {
      if (!lanHost.trim()) throw new Error('프린터 IP 주소를 입력하세요.');
      await API.post('/label-print/raw', { host: lanHost.trim(), port: Number(lanPort) || 9100, data: toBase64(bytes) });
    }
  };

  // 드라이버 없이 ESC/POS로 직접 전송 — 받는 사람별 × 매수
  const doDirectPrint = async () => {
    setBusy(true); setResult(null);
    try {
      const plan = labelPlan(validRecv, nCopies);
      const bytes = (stackMode && plan.length > 1)
        ? canvasToEscPos(stackCanvases(plan.map(({ r, showSender }) => makeCanvas(r, showSender)), stackGap), 1, 3, cut)
        : concatBytes(plan.map(({ r, showSender }) => canvasToEscPos(makeCanvas(r, showSender), 1, 3, cut)));
      await sendBytes(bytes);
      setResult({ ok: true, text: `받는 사람 ${validRecv.length}곳 × ${nCopies}장 = 총 ${validRecv.length * nCopies}장 전송 완료.` });
    } catch (e) {
      setResult({ ok: false, text: (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || String(e) });
    } finally { setBusy(false); }
  };

  const doTest = async () => {
    setBusy(true); setResult(null);
    try {
      if (mode === 'windows') throw new Error('Windows 프린터 방식은 테스트 인쇄를 지원하지 않습니다. [출력]을 눌러 확인하세요.');
      const canvas = renderLabelCanvas({ widthDots, heightDots: 0, fontPt: 14, padXmm: px, padYmm: py, paperDots: Number(paperDots) || 0, shiftMm: num(shiftX, 0), blocks: [{ title: '[테스트 인쇄]', lines: ['IMS 주소출력 연결 확인', '가나다라마바사 ABC 123'] }] });
      await sendBytes(canvasToEscPos(canvas, 1, 3, cut));
      setResult({ ok: true, text: '테스트 인쇄를 전송했습니다.' });
    } catch (e) {
      setResult({ ok: false, text: (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || String(e) });
    } finally { setBusy(false); }
  };

  const bodyHtml = (r, showSender = withSender) => {
    const blocks = [];
    if (showSender) blocks.push(`<div class="blk"><div class="tt">[보내는 사람]</div><div>${esc(senderLine1)}</div><div>${esc(sender.address)}</div></div>`);
    blocks.push(`<div class="blk"><div class="tt">[받는사람]</div><div>${esc(recvLine(r))}</div><div>${esc(r.address)}</div>${r.note ? `<div style="margin-top:10pt">비고: ${esc(r.note)}</div>` : ''}</div>`);
    return `<div class="stamp">${esc(nowStamp())}</div><div class="body">${blocks.join('<div class="sep"></div>')}</div>`;
  };
  const css = `
    @page { size: ${w}mm ${h}mm; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { font-family: 'Malgun Gothic','맑은 고딕',sans-serif; color: #000; }
    .page { width: ${w}mm; height: ${h}mm; box-sizing: border-box; padding: ${py}mm ${px}mm; overflow: hidden; page-break-after: always; display: flex; flex-direction: column; }
    .stamp { font-size: ${Math.max(6, Math.round(fontPt * 0.8))}pt; margin-bottom: 3pt; }
    .body { flex: 1; display: flex; flex-direction: column; justify-content: center; }
    .page:last-child { page-break-after: auto; }
    .blk { font-size: ${fontPt}pt; line-height: 1.45; word-break: keep-all; overflow-wrap: anywhere; }
    .blk .tt { font-weight: 800; margin-bottom: 1mm; }
    .sep { border-top: 0.4mm dashed #000; margin: 4mm 0; }`;

  const doPrint = () => {
    if (!canPrint) { alert('받는 사람을 선택하거나 입력해주세요.'); return; }
    if (mode !== 'windows') { doDirectPrint(); return; }
    const pages = labelPlan(validRecv, nCopies).map(({ r, showSender }) => `<div class="page">${bodyHtml(r, showSender)}</div>`).join('');
    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>주소출력</title><style>${css}</style></head><body>${pages}</body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open(); idoc.write(doc); idoc.close();
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { alert('인쇄 창을 열 수 없습니다.'); }
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 60000);
    }, 300);
  };

  // 헤더에 표시할 연결 프린터 이름
  const printerLabel = (() => {
    if (mode === 'agent') {
      const d = devices.find(x => x.id === device);
      return { text: (d && d.name) || deviceName || '프린터 미설정', ok: !!d };
    }
    if (mode === 'lan') return { text: lanHost ? `네트워크 ${lanHost}:${lanPort}` : '네트워크 (IP 미설정)', ok: !!lanHost };
    if (mode === 'usb') return { text: 'USB 직접 (WebUSB)', ok: true };
    if (mode === 'serial') return { text: `시리얼 ${baud}bps`, ok: true };
    return { text: 'Windows 프린터 (인쇄 창에서 선택)', ok: true };
  })();

  const box = { border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14 };
  const resultBox = result && (
    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, background: result.ok ? '#ecfdf5' : '#fef2f2', color: result.ok ? '#065f46' : '#991b1b' }}>
      {result.text}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 980, maxWidth: '96vw', height: '92vh', display: 'flex', flexDirection: 'column', padding: 24, boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
        {/* 헤더: 제목 / 연결 프린터 이름 / 설정 / 닫기 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>📮 택배 주소 출력</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span title={printerLabel.text} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: printerLabel.ok ? '#065f46' : '#991b1b', background: printerLabel.ok ? '#ecfdf5' : '#fef2f2', borderRadius: 14, padding: '4px 12px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: printerLabel.ok ? '#10b981' : '#ef4444', flex: 'none' }} />
              🖨️ {printerLabel.text}
            </span>
            <button onClick={() => setShowSettings(true)} style={{ ...btnLine, height: 34, fontWeight: 600, color: '#374151' }}>⚙ 설정</button>
            <span onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#999' }}>✕</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
          {/* 좌측: 입력 */}
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingRight: 4 }}>
            {/* 여백 / 글자 크기 / 출력 매수 */}
            <div style={{ ...box, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 0 }}><label style={lbl}>좌우 여백(mm)</label><input style={inp} type="number" min="0" max="30" step="0.5" value={padX} onChange={e => setPadX(e.target.value)} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><label style={lbl}>상하 여백(mm)</label><input style={inp} type="number" min="0" max="30" step="0.5" value={padY} onChange={e => setPadY(e.target.value)} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><label style={lbl}>글자 크기(pt)</label><input style={inp} type="number" min="6" max="40" value={fontPt} onChange={e => setFontPt(Number(e.target.value))} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><label style={lbl}>출력 매수</label><input style={inp} type="number" min="1" max="200" value={copies} onChange={e => setCopies(e.target.value)} title="받는 사람별 출력 매수" /></div>
              <div style={{ flex: 'none' }}>
                <label style={lbl}>배송구분</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {SHIP_TYPES.map(t => (
                    <button key={t} type="button" onClick={() => onShipType(t)}
                      style={{ ...btnLine, height: 38, flex: 'none', padding: '0 14px', ...(shipType === t ? { background: '#1e293b', color: '#fff', borderColor: '#1e293b' } : {}) }}>{t}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* 보내는 사람 */}
            <div style={box}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <b style={{ fontSize: 14 }}>[보내는 사람]</b>
                <label style={{ fontSize: 13, color: '#555', cursor: 'pointer' }}>
                  <input type="checkbox" checked={withSender} onChange={e => setWithSender(e.target.checked)} /> 출력에 포함
                </label>
              </div>
              {withSender && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: '#555', marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>받는 곳이 2곳 이상일 때</span>
                  <label style={{ cursor: 'pointer' }}><input type="radio" name="senderMode" checked={senderMode === 'once'} onChange={() => setSenderMode('once')} /> 한번만 표시 (첫 장)</label>
                  <label style={{ cursor: 'pointer' }}><input type="radio" name="senderMode" checked={senderMode === 'repeat'} onChange={() => setSenderMode('repeat')} /> 장마다 반복 표시</label>
                </div>
              )}
              <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                {loading ? '회사정보를 불러오는 중...' : '환경설정 > 회사정보에서 가져온 값입니다. (이번 출력에 한해 수정 가능)'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1.4 }}><label style={lbl}>회사이름</label><input style={inp} value={sender.name} onChange={e => setSender({ ...sender, name: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lbl}>연락처</label><input style={inp} value={sender.phone} onChange={e => setSender({ ...sender, phone: e.target.value })} /></div>
              </div>
              <label style={lbl}>회사주소</label>
              <input style={inp} value={sender.address} onChange={e => setSender({ ...sender, address: e.target.value })} />
            </div>

            {/* 받는 사람 (다중 선택) */}
            <div style={box}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <b style={{ fontSize: 14 }}>[받는사람] <span style={{ color: '#0F6E56', fontWeight: 600 }}>{receivers.length}곳 선택</span></b>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setReceivers(prev => [...prev, blankRecv(shipType)])} style={{ ...btnLine, height: 28, fontSize: 12.5 }}>+ 직접 입력 추가</button>
                  {receivers.length > 0 && <button onClick={() => setReceivers([])} style={{ ...btnLine, height: 28, fontSize: 12.5, color: '#b91c1c' }}>전체 해제</button>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, position: 'relative' }}>
                <select style={{ ...inp, width: 90, flex: 'none' }} value={category} onChange={e => { setCategory(e.target.value); setShowList(true); }}>
                  <option value="고객사">고객사</option>
                  <option value="협력사">협력사</option>
                </select>
                <input style={inp} placeholder="업체명 / 담당자 검색 (여러 곳 체크 가능)" value={q}
                  onChange={e => { setQ(e.target.value); setShowList(true); }} onFocus={() => setShowList(true)} />
                {showList && (
                  <div style={{ position: 'absolute', top: 42, left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, zIndex: 5, boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #eee', background: '#f8fafc', fontSize: 12.5 }}>
                      <span style={{ color: '#555' }}>체크하면 받는 사람에 추가됩니다 · 선택 {receivers.length}곳</span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button onClick={pickAllHits} style={{ ...btnLine, height: 24, fontSize: 12 }}>검색결과 전체선택</button>
                        <button onClick={() => setShowList(false)} style={{ ...btnLine, height: 24, fontSize: 12, background: '#0F6E56', color: '#fff', border: 'none' }}>완료</button>
                      </span>
                    </div>
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                      {hits.length === 0 && <div style={{ padding: 12, color: '#aaa', fontSize: 13 }}>검색 결과가 없습니다.</div>}
                      {hits.map(c => {
                        const on = isPicked(c);
                        return (
                          <div key={c.id} onClick={() => toggleCompany(c)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13.5, borderBottom: '1px solid #f3f3f3', background: on ? '#F0FDF4' : '#fff', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            <input type="checkbox" checked={on} readOnly style={{ marginTop: 3 }} />
                            <div style={{ minWidth: 0 }}>
                              <b>{c.company_name}</b>
                              <span style={{ color: '#888' }}> {c.manager_name ? `· ${c.manager_name}` : ''} {c.manager_contact ? `(${c.manager_contact})` : ''}</span>
                              <div style={{ fontSize: 12, color: '#aaa' }}>{c.biz_address || '주소 미등록'}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {receivers.length === 0 && <div style={{ fontSize: 13, color: '#aaa', padding: '14px 0', textAlign: 'center' }}>검색해서 받는 사람을 선택하거나 [+ 직접 입력 추가]를 누르세요.</div>}
              {receivers.map((r, i) => (
                <div key={r.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 8, background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0F6E56' }}>받는사람 {i + 1}</span>
                    <span onClick={() => delRecv(r.key)} style={{ cursor: 'pointer', color: '#999', fontSize: 14 }} title="삭제">✕</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <div style={{ flex: 1.4 }}><label style={lbl}>회사이름</label><input style={inp} value={r.name} onChange={e => updRecv(r.key, { name: e.target.value })} /></div>
                    <div style={{ flex: 1 }}><label style={lbl}>담당자</label><input style={inp} value={r.manager} onChange={e => updRecv(r.key, { manager: e.target.value })} /></div>
                    <div style={{ flex: 1 }}><label style={lbl}>연락처</label><input style={inp} value={r.phone} onChange={e => updRecv(r.key, { phone: e.target.value })} /></div>
                  </div>
                  <label style={lbl}>회사주소</label>
                  <input style={inp} value={r.address} onChange={e => updRecv(r.key, { address: e.target.value })} />
                  <label style={{ ...lbl, marginTop: 6 }}>비고</label>
                  <input style={inp} value={r.note || ''} placeholder="택배 / 화물 (수정 가능)" onChange={e => updRecv(r.key, { note: e.target.value })} />
                </div>
              ))}
            </div>

          </div>

          {/* 우측: 전체 미리보기 */}
          <div style={{ flex: '0 0 330px', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#f1f5f9', borderRadius: 10, padding: 12, boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <b style={{ fontSize: 14 }}>미리보기</b>
              <span style={{ fontSize: 12, color: '#64748b' }}>{w}×{h}mm · 여백 {px}/{py}mm · {receivers.length ? `${receivers.length}곳` : '예시'}</span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {previews.map((p, i) => (
                <div key={p.key} style={{ width: '100%', minWidth: 0, flex: 'none' }}>
                  {(p.caption || receivers.length > 1) && <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 3 }}>{p.caption || `${i + 1}. ${p.name || '(이름 없음)'}`}</div>}
                  <img src={p.url} alt="" style={{ width: '100%', maxWidth: '100%', height: 'auto', boxSizing: 'border-box', background: '#fff', padding: '20px 0', border: '1px solid #94a3b8', display: 'block', imageRendering: 'auto' }} />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>실제 출력과 동일한 이미지입니다.</div>
          </div>
        </div>

        {resultBox}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} style={{ background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>닫기</button>
          <button onClick={doPrint} disabled={!canPrint || busy}
            style={{ background: canPrint && !busy ? '#0F6E56' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: canPrint && !busy ? 'pointer' : 'not-allowed' }}>
            {busy ? '전송 중...' : `🖨️ 출력${validRecv.length > 1 ? ` (${validRecv.length}곳 × ${nCopies}장)` : ''}`}
          </button>
        </div>
      </div>

      {/* 설정 모달 */}
      {showSettings && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', padding: 22 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>⚙ 프린터 설정</div>
              <span onClick={() => setShowSettings(false)} style={{ cursor: 'pointer', fontSize: 20, color: '#999' }}>✕</span>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>설정은 이 PC에 자동 저장되어 다음에도 유지됩니다.</div>

            <label style={lbl}>연결 방식</label>
            <select style={{ ...inp, marginBottom: 8 }} value={mode} onChange={e => { setMode(e.target.value); setResult(null); }}>
              {MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            {mode === 'agent' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select style={{ ...inp, flex: 1 }} value={device} onChange={e => setDevice(e.target.value)}>
                  {!devices.find(d => d.id === device) && <option value={device}>{device ? (deviceName || '저장된 프린터') + ' (현재 미감지)' : '프린터 없음 — [찾기]를 누르세요'}</option>}
                  {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <button onClick={findDevices} style={btnLine}>찾기</button>
              </div>
            )}
            {mode === 'lan' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={{ ...inp, flex: 2 }} placeholder="프린터 IP (예: 192.168.0.50)" value={lanHost} onChange={e => setLanHost(e.target.value)} />
                <input style={{ ...inp, flex: 1 }} type="number" placeholder="포트" value={lanPort} onChange={e => setLanPort(e.target.value)} />
              </div>
            )}
            {mode === 'serial' && (
              <select style={{ ...inp, marginBottom: 8 }} value={baud} onChange={e => setBaud(e.target.value)}>
                {[9600, 19200, 38400, 57600, 115200].map(b => <option key={b} value={b}>{b} bps</option>)}
              </select>
            )}
            {mode !== 'windows' && (
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={cut} onChange={e => setCut(e.target.checked)} /> 출력 후 자동 커팅
              </label>
            )}

            <label style={lbl}>라벨 크기</label>
            <select style={{ ...inp, marginBottom: 8 }} value={preset} onChange={e => onPreset(e.target.value)}>
              {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            {preset === 'custom' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={inp} type="number" min="20" value={w} onChange={e => setW(Number(e.target.value))} placeholder="가로(mm)" />
                <input style={inp} type="number" min="20" value={h} onChange={e => setH(Number(e.target.value))} placeholder="세로(mm)" />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 8, marginTop: 4 }}>
              <div style={{ flex: 1 }}><label style={lbl}>좌우 여백(mm)</label><input style={inp} type="number" min="0" max="30" step="0.5" value={padX} onChange={e => setPadX(e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>상하 여백(mm)</label><input style={inp} type="number" min="0" max="30" step="0.5" value={padY} onChange={e => setPadY(e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}><label style={lbl}>글자 크기(pt)</label><input style={inp} type="number" min="6" max="40" value={fontPt} onChange={e => setFontPt(Number(e.target.value))} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>출력 매수</label><input style={inp} type="number" min="1" max="200" value={copies} onChange={e => setCopies(e.target.value)} /></div>
            </div>
            <button onClick={() => { setPadX(DEF_PAD_X); setPadY(DEF_PAD_Y); }} style={{ ...btnLine, height: 30, marginBottom: 8, fontSize: 12.5 }}>여백 기본값({DEF_PAD_X}/{DEF_PAD_Y}mm)</button>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}><label style={lbl}>용지 인쇄폭(dot, 0=라벨폭 / 80mm=576)</label><input style={inp} type="number" min="0" max="832" step="8" value={paperDots} onChange={e => setPaperDots(e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>좌우 위치 보정(mm, +는 오른쪽)</label><input style={inp} type="number" min="-20" max="20" step="0.5" value={shiftX} onChange={e => setShiftX(e.target.value)} /></div>
            </div>

            <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>
              {mode === 'windows'
                ? <>인쇄 창에서 <b>Xprinter</b> 프린터를 선택하고, 용지 크기가 위 라벨 크기와 같은지 확인하세요. (배율 100%, 여백 없음)</>
                : <>Xprinter 정품과 POS-58/80 계열은 같은 ESC/POS 명령을 사용하므로 <b>프린터 드라이버 없이</b> 바로 출력됩니다.</>}
            </div>
            {resultBox}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              {mode !== 'windows' && (
                <button onClick={doTest} disabled={busy}
                  style={{ background: '#fff', color: '#0F6E56', border: '1px solid #0F6E56', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                  테스트 인쇄
                </button>
              )}
              <button onClick={() => setShowSettings(false)} style={{ background: '#0F6E56', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
